/**
 * Page "Événements" — sections par type, onglets à venir / passés
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  BookOpen,
  Palmtree,
  PartyPopper,
  ChevronDown,
  Loader2,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { BottomNav } from '../components/bottom-nav'
import { useDarkMode } from '@/shared/hooks/use-dark-mode'
import { useMyEvents } from '@/features/events/hooks/use-events'
import type { EvenementEtudiantDTO, TypeEvenement } from '@/shared/types/evenement'

// ── Config par type ──────────────────────────────────────

interface TypeConfig {
  label: string
  icon: LucideIcon
  color: string
  colorLight: string
}

function buildTypeConfig(t: (key: string) => string): Record<TypeEvenement, TypeConfig> {
  return {
    CONTROLE: {
      label: t('mobile.events.tests'),
      icon: BookOpen,
      color: '#dc2626',
      colorLight: '#dc262612',
    },
    VACANCES: {
      label: t('mobile.events.vacation'),
      icon: Palmtree,
      color: '#2563eb',
      colorLight: '#2563eb12',
    },
    RELAX: {
      label: t('mobile.events.relax'),
      icon: PartyPopper,
      color: '#d97706',
      colorLight: '#d9770612',
    },
  }
}

const SECTION_ORDER: TypeEvenement[] = ['CONTROLE', 'VACANCES', 'RELAX']

// ── Helpers ──────────────────────────────────────────────

function formatDate(iso: string, locale: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

function getEventTitle(event: EvenementEtudiantDTO, t: (key: string) => string): string {
  switch (event.type) {
    case 'CONTROLE':
      return event.matiereNom ?? t('mobile.events.test')
    case 'VACANCES':
      return t('mobile.events.vacationTitle')
    case 'RELAX':
      return event.libelle || t('mobile.events.relaxTitle')
  }
}

function isEventPast(event: EvenementEtudiantDTO): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const eventDate = new Date(event.dateDebut + 'T00:00:00')
  return eventDate < today
}

function localDarkenColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const f = 0.7
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`
}

function localLightenColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const f = 0.35
  return `rgb(${Math.min(255, Math.round(r + (255 - r) * f))}, ${Math.min(255, Math.round(g + (255 - g) * f))}, ${Math.min(255, Math.round(b + (255 - b) * f))})`
}

// ── Section Header ───────────────────────────────────────

function SectionHeader({
  title, color, icon: Icon, count, collapsed, onToggle, isDark,
}: {
  title: string; color: string; icon: LucideIcon; count: number
  collapsed: boolean; onToggle: () => void; isDark: boolean
}) {
  const dark = isDark ? localLightenColor(color) : localDarkenColor(color)
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-1 pb-2.5"
    >
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: color + '20' }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: dark }} />
      </div>
      <h2 className="text-xs font-bold uppercase tracking-wider" style={{ color: dark }}>
        {title}
      </h2>
      <span
        className="rounded-full px-2 py-0.5 text-[10px] font-bold"
        style={{ backgroundColor: color + '20', color: dark }}
      >
        {count}
      </span>
      <span className="ml-auto">
        <ChevronDown
          className="h-4 w-4 transition-transform"
          style={{ color: dark, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
        />
      </span>
    </button>
  )
}

// ── Event Card ───────────────────────────────────────────

function EventCard({
  event, isPast, onClick, typeConfig,
}: {
  event: EvenementEtudiantDTO; isPast: boolean; onClick: () => void; typeConfig: Record<TypeEvenement, TypeConfig>
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const config = typeConfig[event.type]

  return (
    <div
      onClick={onClick}
      className={cn(
        'relative cursor-pointer overflow-hidden rounded-xl bg-white transition-transform active:scale-[0.98]',
        isPast ? 'opacity-60' : 'shadow-sm',
      )}
    >
      <div
        className="absolute inset-y-0 left-0 w-1 rounded-l-xl"
        style={{ backgroundColor: isPast ? '#9ca3af' : config.color }}
      />
      <div className="flex items-center gap-3 p-3 pl-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={cn('truncate text-sm font-bold', isPast ? 'text-neutral-400' : 'text-neutral-900')}>
              {getEventTitle(event, t)}
            </h3>
            {isPast && (
              <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[9px] font-bold uppercase text-neutral-400">
                {t('mobile.events.pastBadge')}
              </span>
            )}
          </div>
          <p className={cn('mt-0.5 text-xs font-medium', isPast ? 'text-neutral-400' : 'text-neutral-500')}>
            {formatDate(event.dateDebut, locale)}
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────

type Tab = 'upcoming' | 'past'

export function EventsMobilePage() {
  const navigate = useNavigate()
  const { data: events, isLoading } = useMyEvents()
  const { isDark } = useDarkMode()
  const { t } = useTranslation()
  const typeConfig = buildTypeConfig(t)
  const [tab, setTab] = useState<Tab>('upcoming')
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const { upcomingSections, pastSections } = useMemo(() => {
    const allEvents = events ?? []
    const upcoming = allEvents.filter((e) => !isEventPast(e))
    const past = allEvents.filter((e) => isEventPast(e))

    const buildSections = (list: EvenementEtudiantDTO[], sortAsc: boolean) => {
      return SECTION_ORDER
        .map((type) => {
          const items = list
            .filter((e) => e.type === type)
            .sort((a, b) => {
              const cmp = a.dateDebut.localeCompare(b.dateDebut)
              return sortAsc ? cmp : -cmp
            })
          return { type, items }
        })
        .filter((s) => s.items.length > 0)
    }

    return {
      upcomingSections: buildSections(upcoming, true),
      pastSections: buildSections(past, false),
    }
  }, [events])

  const sections = tab === 'upcoming' ? upcomingSections : pastSections
  const isPast = tab === 'past'

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-primary/5 via-primary/[0.02] to-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md">
        <div className="px-4 pt-3">
          {/* Tabs */}
          <div className="flex rounded-xl bg-neutral-100 p-1">
            <button
              onClick={() => setTab('upcoming')}
              className={cn(
                'flex-1 rounded-lg py-2 text-xs font-bold transition-all',
                tab === 'upcoming'
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-400',
              )}
            >
              {t('mobile.events.upcoming')}
              {upcomingSections.reduce((sum, s) => sum + s.items.length, 0) > 0 && (
                <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                  {upcomingSections.reduce((sum, s) => sum + s.items.length, 0)}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab('past')}
              className={cn(
                'flex-1 rounded-lg py-2 text-xs font-bold transition-all',
                tab === 'past'
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-400',
              )}
            >
              {t('mobile.events.past')}
              {pastSections.reduce((sum, s) => sum + s.items.length, 0) > 0 && (
                <span className="ml-1.5 rounded-full bg-neutral-200 px-1.5 py-0.5 text-[10px] font-bold text-neutral-500">
                  {pastSections.reduce((sum, s) => sum + s.items.length, 0)}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="mt-3 h-px bg-neutral-100" />
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-24 pt-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : sections.length === 0 ? (
          <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-neutral-200 px-6 py-10">
            <span className="text-3xl">{isPast ? '📋' : '📭'}</span>
            <p className="mt-2 text-sm text-neutral-400">
              {isPast ? t('mobile.events.noPast') : t('mobile.events.noUpcoming')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sections.map((section) => {
              const config = typeConfig[section.type]
              const isCollapsed = collapsedSections.has(`${tab}-${section.type}`)
              return (
                <div
                  key={`${tab}-${section.type}`}
                  className="rounded-2xl p-3"
                  style={{ backgroundColor: config.colorLight }}
                >
                  <SectionHeader
                    title={config.label}
                    color={config.color}
                    icon={config.icon}
                    count={section.items.length}
                    collapsed={isCollapsed}
                    onToggle={() => toggleSection(`${tab}-${section.type}`)}
                    isDark={isDark}
                  />

                  {!isCollapsed && (
                    <div className="space-y-2">
                      {section.items.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          isPast={isPast}
                          onClick={() => navigate(`/events/${event.id}/edit`)}
                          typeConfig={typeConfig}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="fixed inset-x-4 bottom-20 z-50">
        <button
          onClick={() => navigate('/events/create')}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 text-sm font-bold text-white shadow-xl shadow-primary/30 transition-all active:scale-[0.98]"
        >
          <Plus className="h-5 w-5" strokeWidth={2.5} />
          {t('mobile.events.newEvent')}
        </button>
      </div>

      <BottomNav />
    </div>
  )
}
